import React from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { toast } from "react-toastify";
import * as API from "../../../lib/api.js";
import UploadBase64 from "../../../lib/builder/base64.js";
import { GenerateDomain } from "../../../lib/domain.js";
import { AiGenerateECommerce } from "../../../lib/e-commerce.js";
import * as Links from "../../../lib/links.js";
import { CountryCode2Locale } from "../../../lib/localisations.js";
import BackNext from "../BackNext.jsx";
import { ON_DESIGNING_END_TIMEOUT } from "../DesigningLayout.jsx";
import BusinessName from "./BusinessName.jsx";
import PhotosNVideos from "./PhotosNVideos.jsx";
import ProductDescription from "./ProductDescription.jsx";
import TechnicalDetails from "./TechnicalDetails.jsx";

export default function WStepEcommerceDetails(props) {
    const { t } = useTranslation();
    const history = useHistory();

    const {
        set,
        goto,
        addDLPromise, // ecommerce + page
        gotoLastPart, // ecommerce + page
        variables,
        variables: { SEOLocation, Localisation },

        partKey,
    } = props;

    const _allowGenerate =
        variables?.BusinessName?.length > 0 &&
        variables?.ProductDescription?.length > 0 &&
        variables?.PhotosNVideos?.length > 0 &&
        (() => {
            const yt = variables.PhotosNVideos.filter(({ type }) => type === "youtube")?.[0];
            return !yt || !!yt?.id;
        })();

    React.useEffect(() => {
        if (!Localisation && SEOLocation) {
            const locale = CountryCode2Locale[SEOLocation].split("-")[0];

            set({
                ...variables,
                Localisation: locale,
            });
        }
    }, [Localisation]);

    // AI Generate
    async function _aiGenerate(idProject) {
        return await AiGenerateECommerce(idProject);
    }

    // Compile/Generate
    async function _compileGenerate(variables, structure) {
        return await API.CompileGenerate({ variables, structure })
            .then(({ variables: updVariables, structure: updStructure }) => ({ updVariables, updStructure }))
            .then(({ updVariables, updStructure }) => {
                return { updVariables, updStructure };
            })
            .catch(e => {
                if (e.name !== "AbortError" && e.message !== "Error while saving.") {
                    console.warn(`[ERROR] onDesigningEnd(): ${e.message} (${e.name})`);
                }
            });
    }

    // Set project/page
    async function _setProject(idProject, idPage, variables, structure) {
        let Project = {
            id_project: idProject || null,
            domain: GenerateDomain(),
            variables,
        };

        let Page = { structure };

        const link = await API.ProjectSet(Project)
            .then(res => {
                if (res?.errors) {
                    for (const error of res.errors) {
                        if (error?.error === "LIMIT_REACHED") {
                            toast.error(
                                t(
                                    variables?.ProductType === "ecommerce"
                                        ? "CurrentPlanDoesNotAllowYouToCreateECommerce"
                                        : "CurrentPlanDoesNotAllowYouToCreateWebsites"
                                ),
                                { autoClose: 30000 }
                            );

                            history.push(Links.Subscription());

                            throw { type: "abort" };
                        }

                        toast.error(error?.error || error, { autoClose: 30000 });
                    }

                    console.error(`[ERROR]`, res.errors);
                    throw new Error(`Errors above!`);
                }

                return res;
            })

            .then(res => (Project = { ...Project, ...res }))
            .then(() => variables.svgBg && API.SvgBgSign(variables.svgBg))
            .then(() => API.PageSet(Project.id_project, Page.structure, idPage))
            .then(res => (Page = { ...Page, ...res }))
            .then(() => UploadBase64({ id_project: Project.id_project, variables }))
            .then(() => API.ProjectUpdate(Project))
            .then(() => API.WizardClear())
            .then(() => Links.WebsiteEditDesign(Project.id_project))
            .catch(e => {
                if (e?.type === "abort") {
                    return false;
                }

                toast.error(e.message);
            });

        return { Project, Page, link };
    }

    // Main
    async function _generate() {
        // Promise for DesigningLayout (doneN() change designing layout step)
        let done1, done2;
        addDLPromise(new Promise(resolve => (done1 = resolve)));
        addDLPromise(new Promise(resolve => (done2 = resolve)));

        // Show designing layout
        gotoLastPart();

        try {
            // Set project
            const resultSetProject = await _setProject(null, null, variables, []);
            if (!resultSetProject) {
                return;
            }

            const idProject = resultSetProject?.Project?.id_project;
            const idPage = resultSetProject?.Page?.id_page;
            if (!idProject || !idPage) {
                toast.error("Unknown Error =(");
                goto(partKey);
                return false;
            }

            // AI
            const resultAiGenerate = await _aiGenerate(idProject);
            if (resultAiGenerate?.errors) {
                await API.ProjectDelete(idProject);

                for (const error of resultAiGenerate?.errors) {
                    if (error?.error === "TIMEOUT") {
                        toast.error(t("TIMEOUT"));
                    } else {
                        toast.error(error?.error || error);
                    }
                }

                goto(partKey);

                return false;
            }

            const { aiVariables, aiStructure } = resultAiGenerate;

            // Change designing layout step
            done1();
            // Compile/Generate

            const { updVariables, updStructure } = await _compileGenerate(
                { ...variables, ...aiVariables }, // merge
                aiStructure
            );
            // Update project

            const { Project, Page, link } = await _setProject(idProject, idPage, updVariables, updStructure);

            // Change designing layout step
            done2();

            setTimeout(() => history.push(link), ON_DESIGNING_END_TIMEOUT); // DesigningLayout
        } catch (e) {
            console.log("ERROR >", e);
            toast.error(e.message);
            goto(partKey);
        }
    }

    // Render --------------------------------------------------------------- //
    const ECProps = { set, variables };

    return (
        <div className="wizard-part-inner" style={{ width: 600, gridGap: "40px" }}>
            <p>{t("Tell us about your offer so we can generate a professional product page for you.")}</p>

            <div className="grid-gap-20 align-content-start align-items-center">
                <BusinessName {...ECProps} />
                <ProductDescription {...ECProps} />
                <PhotosNVideos {...ECProps} />
                <TechnicalDetails {...ECProps} />
            </div>

            <BackNext {...props} generate={_allowGenerate && _generate} next={null} />
        </div>
    );
}
